import {
  GreaterAreaDropdown,
  NeighborhoodDropdown,
  OrganizationDropdown,
  StateField,
} from '@/components/dropdowns';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import { VideoUploadDropzone } from '@/components/shared/VideoUploadDropzone';
import type { Hotel } from '@/config/objects/hotels';
import { cn } from '@/lib/utils';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EntityFormShell,
  FormFieldGroup,
  Input,
  RichTextEditor,
  SwitchField,
  toast,
  useEntityForm,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { TriangleAlert } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

interface HotelFormData extends Record<string, unknown> {
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description: string;
  featuredImageId: string | undefined;
  specialVideoId: string | undefined;
  organizationId: string | undefined;
  phoneNumber: string;
  email: string;
  website: string;
  greaterAreaId: string | undefined;
  neighborhoodId: string | undefined;
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: string;
  longitude: string;
  timezone: string;
}

const DEFAULTS: HotelFormData = {
  name: '',
  status: 'published',
  description: '',
  featuredImageId: undefined,
  specialVideoId: undefined,
  organizationId: undefined,
  phoneNumber: '',
  email: '',
  website: '',
  greaterAreaId: undefined,
  neighborhoodId: undefined,
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  latitude: '',
  longitude: '',
  timezone: '',
};

export function HotelFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isInternal } = useRequireOrganization();
  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const form = useEntityForm<HotelFormData, Hotel>({
    resource: 'hotels',
    itemId,
    defaults: DEFAULTS,
    queryKey: 'Hotel',
    fromApi: (h) => ({
      name: h.name ?? '',
      status: h.status ?? 'published',
      description: h.description ?? '',
      featuredImageId: h.featuredImageId ?? undefined,
      specialVideoId: h.specialVideoId ?? undefined,
      organizationId: h.organizationId ?? undefined,
      phoneNumber: h.phoneNumber ?? '',
      email: h.email ?? '',
      website: h.website ?? '',
      greaterAreaId: h.greaterAreaId ?? undefined,
      neighborhoodId: h.neighborhoodId ?? undefined,
      streetAddress: h.streetAddress ?? '',
      streetAddress2: h.streetAddress_2 ?? '',
      city: h.city ?? '',
      state: h.state ?? '',
      postalCode: h.postalCode ?? '',
      country: h.country ?? 'US',
      latitude: h.latitude != null ? String(h.latitude) : '',
      longitude: h.longitude != null ? String(h.longitude) : '',
      timezone: h.timezone ?? '',
    }),
    toApi: (f) => ({
      name: f.name.trim(),
      status: f.status,
      description: f.description || null,
      featuredImageId: f.featuredImageId || null,
      specialVideoId: f.specialVideoId || null,
      organizationId: f.organizationId || null,
      phoneNumber: f.phoneNumber.trim() || null,
      email: f.email.trim() || null,
      website: f.website.trim() || null,
      greaterAreaId: f.greaterAreaId || null,
      neighborhoodId: f.neighborhoodId || null,
      streetAddress: f.streetAddress.trim() || '',
      streetAddress2: f.streetAddress2.trim() || null,
      city: f.city.trim() || '',
      state: f.state.trim() || '',
      postalCode: f.postalCode.trim() || null,
      country: f.country.trim() || '',
      latitude: f.latitude ? parseFloat(f.latitude) : null,
      longitude: f.longitude ? parseFloat(f.longitude) : null,
    }),
    validate: (f) => {
      const errs: Partial<Record<keyof HotelFormData, string>> = {};
      if (!f.name.trim()) errs.name = 'Name is required';
      if (!f.streetAddress.trim()) errs.streetAddress = 'Street address is required';
      if (!f.city.trim()) errs.city = 'City is required';
      if (!f.state.trim()) errs.state = 'State is required';
      if (!f.country.trim()) errs.country = 'Country is required';
      if (!f.greaterAreaId) errs.greaterAreaId = 'Greater area is required';
      if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
        errs.email = 'Must be a valid email address';
      }
      if (f.website.trim()) {
        try {
          new URL(f.website.trim());
        } catch {
          errs.website = 'Must be a valid URL (include https://)';
        }
      }
      return errs;
    },
    onSuccess: (entity, { isEdit: wasEdit }) => {
      if (wasEdit) {
        toast.success('Hotel saved successfully');
        navigate(-1);
      } else {
        toast.success('Hotel created successfully');
        if (entity.id) navigate(`/hotels/${entity.id}`);
      }
    },
    onNotFound: () => {
      toast.error('Hotel not found');
      navigate('/hotels');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to save hotel');
    },
  });

  return (
    <EntityFormShell
      title={isEdit ? 'Edit Hotel' : 'Create Hotel'}
      subtitle={
        isEdit
          ? form.formData.name || 'Update hotel details'
          : 'Add a new hotel property to the platform'
      }
      backHref={isEdit && itemId ? `/hotels/${itemId}` : '/hotels'}
      form={form}
    >
      <div className="space-y-6">
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Basic Information</CardTitle>
            <CardDescription>Core hotel details and identification</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup name="name" label="Name" required error={form.errors.name}>
              <Input
                id="name"
                value={form.formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder="Enter hotel name"
                className={cn(form.errors.name && 'border-destructive')}
              />
            </FormFieldGroup>

            <ImageUploadDropzone
              targetAspectRatio={4 / 3}
              value={form.formData.featuredImageId}
              onChange={(value) => form.updateField('featuredImageId', value)}
            />

            <SwitchField
              label="Published"
              description="Make this hotel visible to users in the app"
              checked={form.formData.status === 'published'}
              onCheckedChange={(checked) =>
                form.updateField('status', checked ? 'published' : 'draft')
              }
            />

            {isInternal && (
              <FormFieldGroup name="organizationId" label="Organization">
                <OrganizationDropdown
                  value={form.formData.organizationId ?? ''}
                  onChange={(value) => form.updateField('organizationId', value || undefined)}
                  placeholder="Select Organization"
                />
              </FormFieldGroup>
            )}

            <FormFieldGroup name="description" label="Description">
              <RichTextEditor
                value={form.formData.description}
                onChange={(value) => form.updateField('description', value ?? '')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Hotel Video</CardTitle>
            <CardDescription>
              An optional vertical video for this hotel. When set, it overrides the
              global how-to video in this hotel&apos;s app.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <VideoUploadDropzone
              label="Special video"
              value={form.formData.specialVideoId}
              onChange={(value) => form.updateField('specialVideoId', value)}
            />
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Contact Information</CardTitle>
            <CardDescription>How guests can reach the hotel</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="phoneNumber" label="Phone Number">
                <Input
                  id="phoneNumber"
                  value={form.formData.phoneNumber}
                  onChange={(e) => form.updateField('phoneNumber', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </FormFieldGroup>

              <FormFieldGroup name="email" label="Email" error={form.errors.email}>
                <Input
                  id="email"
                  type="email"
                  value={form.formData.email}
                  onChange={(e) => form.updateField('email', e.target.value)}
                  placeholder="hello@hotel.com"
                  className={cn(form.errors.email && 'border-destructive')}
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup name="website" label="Website" error={form.errors.website}>
              <Input
                id="website"
                value={form.formData.website}
                onChange={(e) => form.updateField('website', e.target.value)}
                placeholder="https://example.com"
                className={cn(form.errors.website && 'border-destructive')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Location</CardTitle>
            <CardDescription>Physical address and geographic classification</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup
                name="greaterAreaId"
                label="Greater Area"
                required
                error={form.errors.greaterAreaId}
              >
                <GreaterAreaDropdown
                  value={form.formData.greaterAreaId ?? ''}
                  onChange={(value) => {
                    form.updateField('greaterAreaId', value || undefined);
                    form.updateField('neighborhoodId', undefined);
                  }}
                  placeholder="Select Greater Area"
                  className={cn(form.errors.greaterAreaId && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="neighborhoodId" label="Neighborhood">
                <NeighborhoodDropdown
                  value={form.formData.neighborhoodId ?? ''}
                  onChange={(value) => form.updateField('neighborhoodId', value || undefined)}
                  placeholder="Select Neighborhood"
                  greaterAreaId={form.formData.greaterAreaId}
                  disabled={!form.formData.greaterAreaId}
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup
              name="streetAddress"
              label="Street Address"
              required
              error={form.errors.streetAddress}
            >
              <Input
                id="streetAddress"
                value={form.formData.streetAddress}
                onChange={(e) => form.updateField('streetAddress', e.target.value)}
                placeholder="123 Main Street"
                className={cn(form.errors.streetAddress && 'border-destructive')}
              />
            </FormFieldGroup>

            <FormFieldGroup name="streetAddress2" label="Address Line 2">
              <Input
                id="streetAddress2"
                value={form.formData.streetAddress2}
                onChange={(e) => form.updateField('streetAddress2', e.target.value)}
                placeholder="Suite, unit, floor, etc."
              />
            </FormFieldGroup>

            <div className="grid gap-6 md:grid-cols-3">
              <FormFieldGroup name="city" label="City" required error={form.errors.city}>
                <Input
                  id="city"
                  value={form.formData.city}
                  onChange={(e) => form.updateField('city', e.target.value)}
                  placeholder="City"
                  className={cn(form.errors.city && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="state" label="State" required error={form.errors.state}>
                <StateField
                  value={form.formData.state}
                  onChange={(value) => form.updateField('state', value)}
                  country={form.formData.country}
                  placeholder="Select State"
                  className={cn(form.errors.state && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="postalCode" label="Postal Code">
                <Input
                  id="postalCode"
                  value={form.formData.postalCode}
                  onChange={(e) => form.updateField('postalCode', e.target.value)}
                  placeholder="ZIP Code"
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup
              name="country"
              label="Country"
              required
              error={form.errors.country}
            >
              <Input
                id="country"
                value={form.formData.country}
                onChange={(e) => form.updateField('country', e.target.value)}
                placeholder="Country"
                className={cn(form.errors.country && 'border-destructive')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Coordinates</CardTitle>
            <CardDescription>Geographic coordinates for map display</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <Alert variant="warning">
              <TriangleAlert className="h-4 w-4" />
              <AlertDescription>
                These values are auto-filled from the hotel address. Only modify them if you need
                to tweak the exact pin position on the map.
              </AlertDescription>
            </Alert>

            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="latitude" label="Latitude">
                <Input
                  id="latitude"
                  value={form.formData.latitude}
                  onChange={(e) => form.updateField('latitude', e.target.value)}
                  placeholder="e.g. 42.3601"
                />
              </FormFieldGroup>

              <FormFieldGroup name="longitude" label="Longitude">
                <Input
                  id="longitude"
                  value={form.formData.longitude}
                  onChange={(e) => form.updateField('longitude', e.target.value)}
                  placeholder="e.g. -71.0589"
                />
              </FormFieldGroup>
            </div>

            {form.formData.timezone && (
              <FormFieldGroup
                name="timezone"
                label="Timezone"
                description="Auto-detected from coordinates"
              >
                <Input value={form.formData.timezone} readOnly className="bg-muted" />
              </FormFieldGroup>
            )}
          </CardContent>
        </Card>
      </div>
    </EntityFormShell>
  );
}
